import React, { useMemo, useRef } from 'react';

import ChartsIndex from '@/pages/intelligence/components/ChartsComponents/ChartsIndex';
import useLineCharts from '@/pages/intelligence/components/Chart/hooks/useLineCharts';
import CardStats from '@/pages/intelligence/pages/TopCharts/MobileGames/components/GamesDetails/Stats/components/CardStats';
import { computeRankAxisOverrides } from '@/pages/intelligence/utils/storeRankAxisUtils';
import {
  getRobloxRankChartXAxisDates,
  transformRobloxRankChartLineSeries,
} from '../../utils/transformRobloxRankChartData';
import { formatRobloxRankChartYAxisLabel } from '@intelligence/v3/utils/robloxChartAxisFormat';
import { LINE_GRID_TOP } from '@intelligence/pages/TopCharts/_config/echarts';
import { buildRobloxLineChartOption } from '@intelligence/v3/utils/buildRobloxLineChartOption';
import { useRobloxLineChartTooltipFix } from '@intelligence/v3/_hooks/useRobloxLineChartTooltipFix';

import type { IChartsIndexInstance } from '@/pages/intelligence/components/ChartsComponents/ChartsIndex';

import type { TableSelectionType } from '@intelligence/components/TableComponents/components/TableCheckbox';
import type { IRobloxGameRankRow } from '../../types';

import './index.scss';

const CHART_HEIGHT = 350;
const CHART_METRIC = 'rank';

interface IRobloxGameRankLineChartProps {
  loading: boolean;
  chartList: IRobloxGameRankRow[];
  tableSelection: TableSelectionType[];
  selectedSource: string;
  chartTitle: string;
  granularity?: IGranularity;
}

const RobloxGameRankLineChart: React.FC<IRobloxGameRankLineChartProps> = ({
  loading,
  chartList,
  tableSelection,
  selectedSource,
  chartTitle,
  granularity = 'daily',
}) => {
  const lineSeriesList = useMemo(
    () => transformRobloxRankChartLineSeries(chartList, tableSelection, selectedSource),
    [chartList, tableSelection, selectedSource],
  );

  const lineXAxisData = useMemo(
    () => getRobloxRankChartXAxisDates(chartList, selectedSource),
    [chartList, selectedSource],
  );

  const lineSeriesData = useMemo(() => ({
    [CHART_METRIC]: lineSeriesList,
  }), [lineSeriesList]);

  const { lineOption } = useLineCharts({
    lineData: lineSeriesData,
    metric: CHART_METRIC,
    lineXAxisData,
    topChartsType: 'detailStorerank',
    imageType: 'region',
    titleNow: chartTitle,
    yAxisFormatter: formatRobloxRankChartYAxisLabel,
    gridConfig: {
      top: LINE_GRID_TOP,
    },
  });

  const storeRankValues = useMemo(() => {
    const values: number[] = [];
    lineSeriesList.forEach((seriesItem) => {
      seriesItem?.history?.forEach((historyItem) => {
        const value = Number(historyItem?.value);
        if (Number.isFinite(value)) {
          values.push(value);
        }
      });
    });
    return values;
  }, [lineSeriesList]);

  const rankAxisOverrides = useMemo(
    () => computeRankAxisOverrides(storeRankValues, 1),
    [storeRankValues],
  );

  const lineOptionCur = useMemo(() => {
    const baseOption = buildRobloxLineChartOption(lineOption, granularity) as Record<string, any> | undefined;
    if (!baseOption) {
      return baseOption;
    }

    return {
      ...baseOption,
      yAxis: {
        ...baseOption.yAxis,
        inverse: true,
        min: rankAxisOverrides.min,
        max: rankAxisOverrides.max ?? undefined,
        minInterval: 1,
        interval: rankAxisOverrides.interval ?? undefined,
      },
    };
  }, [lineOption, rankAxisOverrides, granularity]);

  const isNoData = useMemo(() => {
    if (!lineSeriesList.length) {
      return true;
    }

    return lineSeriesList.every((seriesItem) => !seriesItem?.history?.length);
  }, [lineSeriesList]);

  const chartRef = useRef<IChartsIndexInstance>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useRobloxLineChartTooltipFix({
    chartRef,
    wrapperRef,
    enabled: !loading && !isNoData,
    deps: [lineOptionCur, chartList, tableSelection, selectedSource],
  });

  if (loading) {
    return (
      <CardStats
        cardStatus={{ isLoading: true }}
        height={CHART_HEIGHT}
      />
    );
  }

  if (isNoData) {
    return (
      <CardStats
        cardStatus={{ isNoData: true }}
        height={CHART_HEIGHT}
      />
    );
  }

  return (
    <div ref={wrapperRef} className="dbi-roblox-game-rank-line-chart card-line">
      <ChartsIndex
        ref={chartRef}
        chartLoading={false}
        lineOption={lineOptionCur}
        chartType="line"
        isNodata={isNoData}
        echartHight={CHART_HEIGHT}
      />
    </div>
  );
};

export default RobloxGameRankLineChart;
